import React, { useEffect, useState } from "react";

import ButtonV2 from "../components/buttonV2";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";

import { motion } from "framer-motion";

import { useMediaQuery } from "../../hooks";
import { FaBars, FaTimes } from "react-icons/fa";

import {
  CHEVRON_DOWN,
  CHEVRON_RIGHT_WHITE,
  CHEVRON_WHITE,
  CUSTOMARY_LOGO,
  FACEBOOK,
  FRAME_1,
  HOME_ICON,
  INSTAGRAM,
  LINKEDIN,
  TWITTER,
} from "../../assets";
import { selectToken } from "../../states/slices/authReducer";
import { footer } from "../../constants";
import useDropdown from "../../utils-func/functions/usedropdown";

const LinkDropDown = ({
  items,
  open,
  close,
}: {
  items: Array<{ label: string; link: string }>;
  open: boolean;
  close: () => void;
}) => {
  return (
    <>
      {open && (
        <div
          className={`min-w-fit text-nowrap top-10 p-3 rounded-lg bg-white absolute shadow-blur h-fit flex flex-col items-start gap-3 z-50`}
        >
          {items.map((l) => (
            <Link
              to={`/${l.link}`}
              key={l.link}
              onClick={close}
              className="text-primary-300 hover:bg-gray-100 w-full p-1.5 rounded-lg font-medium text-sm"
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
};

const FooterLinks = ({
  title,
  links,
}: {
  title: string;
  links: Array<{ title: string; link: string }>;
}) => (
  <div className="flex flex-col gap-y-3 text-white">
    <span className="font-medium text-[16px] border-b-2 border-white w-[30px] text-nowrap">
      {title}
    </span>
    <ul className="flex flex-col gap-y-3 max-w-[280px]">
      {links.map((item) => (
        <Link to={item.link} className="text-sm font-normal">
          {item.title}
        </Link>
      ))}
    </ul>
  </div>
);

function LandingPageLayout({
  children,
  title,
  subLink,
  currentLink,
}: {
  children: React.ReactNode;
  title: JSX.Element;
  currentLink: string;
  subLink?: string;
}) {
  const location = useLocation();
  const { pathname } = location;
  return (
    <>
      <div
        id="hero-section"
        className="w-full relative lg:h-screen h-[480px] overflow-hidden"
      >
        <div className="w-full h-full absolute inset-0">
          <img src={FRAME_1} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-[#00000099] z-10"></div>

        <Header />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute flex-col gap-y-4 inset-0 lg:mt-7 mt-20 flex z-40 items-center text-white justify-center"
        >
          <span className="lg:text-[43px] text-3xl font-bold uppercase text-center lg:leading-[60px] leading-[40px]">
            {/* A Brief History and Evolution of <br /> the Enugu State Customary{" "}
          <br /> Court of Appeal */}
            {title}
          </span>
          <div className="lg:text-[16px] flex items-center gap-x-2 text-sm font-medium text-center">
            <Link to={"/"} className="flex items-center gap-x-2">
              <img src={HOME_ICON} className="w-[20px] h-[20px]" alt="" />
              <span>Home</span>
            </Link>
            <img
              src={CHEVRON_RIGHT_WHITE}
              className="w-[16px] h-[16px]"
              alt=""
            />
            <span>{currentLink}</span>
            {subLink && (
              <>
                <img
                  src={CHEVRON_RIGHT_WHITE}
                  className="w-[16px] h-[16px]"
                  alt=""
                />
                <span>{subLink}</span>
              </>
            )}
          </div>
        </motion.div>
      </div>
      <div>{children}</div>
      <Footer />
    </>
  );
}
export default LandingPageLayout;

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const mobile = useMediaQuery("(max-width: 640px)");
  const tablet = useMediaQuery("(max-width: 1024px)");
  const [open, setOpen] = useState(false);
  const { dropdownRef, setDropdowns, closeDropDown, dropdowns } = useDropdown();

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight =
        document.getElementById("hero-section")?.offsetHeight || 300;
      const scrollY = window.scrollY;
      setScrolled(scrollY > heroHeight - 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (open && mobile) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [open, mobile]);

  const navigate = useNavigate();

  // const handleScrollToElem = (id: string) => {
  //   const elem = document.getElementById(id);
  //   if (elem) {
  //     elem.scrollIntoView({ behavior: "smooth" });
  //   }
  // };

  const location = useLocation();
  const { pathname } = location;

  return (
    <>
      {mobile ? (
        <div
          className={`w-full top-0 z-50 flex items-center justify-between px-4 pt-4 pb-4 fixed ${
            scrolled || pathname === "/privacy-policy" || pathname === "/search"
              ? "bg-white shadow-md"
              : open
                ? "bg-black/90 backdrop-blur-sm"
                : "bg-transparent"
          } transition-all duration-300`}
        >
          <Link to={"/"}>
            <img
              src={CUSTOMARY_LOGO}
              className="w-[100px] h-[38px] sm:w-[113.62px] sm:h-[42px]"
              alt="Customary Court Logo"
            />
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}
            className="p-2 rounded-md hover:bg-gray-100/10 transition-colors"
            aria-label="Toggle menu"
          >
            {open ? (
              <FaTimes
                size={20}
                color={
                  scrolled || pathname === "/privacy-policy" ? "black" : "white"
                }
              />
            ) : (
              <FaBars
                size={20}
                color={
                  scrolled || pathname === "/privacy-policy" ? "black" : "white"
                }
              />
            )}
          </button>

          <Transition
            show={open}
            enter="transform transition-all ease-out duration-300"
            enterFrom="opacity-0 -translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="transform transition-all ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-4"
          >
            <div
              className={`absolute ${
                scrolled || pathname === "/privacy-policy"
                  ? "bg-white shadow-lg"
                  : "bg-black/95 backdrop-blur-sm"
              } w-full min-h-[300px] flex items-start flex-col top-full z-50 left-0 p-6 gap-y-6`}
            >
              <div
                className={`flex items-start flex-col space-y-6 w-full ${
                  scrolled || pathname === "/privacy-policy"
                    ? "text-black"
                    : "text-white"
                } transition-colors duration-300 font-medium text-[16px]`}
              >
                <Link
                  to="/"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  Home
                </Link>
                <Link
                  to="/court-history"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  Court History
                </Link>
                <Link
                  to="/president-judges"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  President & Judges
                </Link>
                <Link
                  to="/chief-registrar"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  Chief Registrar
                </Link>
                <Link
                  to="/court-documents"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  Court Documents
                </Link>
                <Link
                  to="/cause-list"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  Cause List
                </Link>
                <Link
                  to="/publications"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  Publications
                </Link>
                <Link
                  to="/news"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  News & Events
                </Link>
                <Link
                  to="/photo-gallery"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  Photo Gallery
                </Link>
                <Link
                  to="/faqs"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2 border-b border-gray-200/20"
                >
                  FAQ
                </Link>
                <Link
                  to="/contact-us"
                  onClick={() => setOpen(false)}
                  className="cursor-pointer hover:text-globalGreen transition-all duration-300 w-full py-2"
                >
                  Contact Us
                </Link>
              </div>

              <div className="flex flex-col gap-y-3 w-full pt-4 border-t border-gray-200/20">
                <ButtonV2
                  btnStyle={`w-full p-3 ${
                    scrolled || pathname === "/privacy-policy"
                      ? "border-globalGreen bg-transparent"
                      : "border-white bg-transparent"
                  } h-fit px-4 py-3 rounded-3xl border flex items-center justify-center`}
                  textStyle={`${
                    scrolled || pathname === "/privacy-policy"
                      ? "text-globalGreen"
                      : "text-white"
                  } font-semibold text-[16px]`}
                  title="Sign in"
                  handleClick={() => {
                    navigate("/login", { replace: true });
                    setOpen(false);
                  }}
                />

                <ButtonV2
                  btnStyle="w-full h-fit px-4 py-3 rounded-3xl bg-globalGreen flex items-center justify-center"
                  textStyle="text-white font-semibold text-[16px]"
                  title="Sign up"
                  handleClick={() => {
                    navigate("/sign-up", { replace: true });
                    setOpen(false);
                  }}
                />
              </div>
            </div>
          </Transition>
        </div>
      ) : (
        <div
          className={`w-full fixed left-0 px-4 sm:px-8 lg:px-10 py-4 lg:py-7 z-50 top-0 right-0 flex items-center justify-between ${
            scrolled || pathname === "/privacy-policy" || pathname === "/search"
              ? "bg-white shadow-md"
              : "bg-transparent"
          } transition-colors duration-300`}
        >
          <Link to={"/"}>
            <img
              src={CUSTOMARY_LOGO}
              className="w-[40px] h-[40px] lg:w-[45px] lg:h-[45px]"
              alt="Customary Court Logo"
            />
          </Link>

          <div
            className={`hidden lg:flex items-center justify-center space-x-7 ${
              scrolled ||
              pathname === "/privacy-policy" ||
              pathname === "/search"
                ? "text-black"
                : "text-white"
            } transition-colors duration-300 font-normal text-sm`}
          >
            <Link
              to={"/"}
              className="cursor-pointer hover:text-globalGreen transition-all duration-300"
            >
              Home
            </Link>
            <div
              ref={dropdownRef.about}
              onClick={() => setDropdowns({ ...dropdowns, about: true })}
              className="cursor-pointer relative flex items-center gap-x-2 transition-all duration-300"
            >
              <span>About</span>
              <img
                src={scrolled ? CHEVRON_DOWN : CHEVRON_WHITE}
                className="w-[20px] h-[20px]"
                alt=""
              />
              <LinkDropDown
                open={dropdowns.about}
                close={() => closeDropDown("about")}
                items={[
                  { label: "Court History", link: "court-history" },
                  { label: "The President & Judges", link: "president-judges" },
                ]}
              />
            </div>
            <div
              ref={dropdownRef.management}
              onClick={() => setDropdowns({ ...dropdowns, management: true })}
              className="cursor-pointer relative flex items-center gap-x-2 transition-all duration-300"
            >
              <span>Management</span>
              <img
                src={scrolled ? CHEVRON_DOWN : CHEVRON_WHITE}
                className="w-[20px] h-[20px]"
                alt=""
              />
              <LinkDropDown
                open={dropdowns.management}
                close={() => closeDropDown("management")}
                items={[
                  {
                    label: "Chief Registrar & Deputy Chief Registrars",
                    link: "chief-registrar",
                  },
                  { label: "Admin & Management", link: "admin" },
                ]}
              />
            </div>
            <Link
              to={"/court-documents"}
              className="cursor-pointer hover:text-globalGreen transition-all duration-300"
            >
              Court Documents
            </Link>
            <Link
              to={"/cause-list"}
              className="cursor-pointer hover:text-globalGreen transition-all duration-300"
            >
              Cause List
            </Link>
            <Link
              to={"/publications"}
              className="cursor-pointer hover:text-globalGreen transition-all duration-300"
            >
              Publications
            </Link>
            <div
              ref={dropdownRef.media}
              onClick={() => setDropdowns({ ...dropdowns, media: true })}
              className="cursor-pointer relative flex items-center gap-x-2 transition-all duration-300"
            >
              <span>Media</span>
              <img
                src={scrolled ? CHEVRON_DOWN : CHEVRON_WHITE}
                className="w-[20px] h-[20px]"
                alt=""
              />
              <LinkDropDown
                open={dropdowns.media}
                close={() => closeDropDown("media")}
                items={[
                  {
                    label: "News & Events",
                    link: "news",
                  },
                  {
                    label: "Photo Gallery",
                    link: "photo-gallery",
                  },
                  { label: "Videos", link: "videos" },
                ]}
              />
            </div>
            <Link
              to={"/faqs"}
              className="cursor-pointer hover:text-globalGreen transition-all duration-300"
            >
              FAQ
            </Link>
            <Link
              to={"/contact-us"}
              className="cursor-pointer hover:text-globalGreen transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-x-4">
            <ButtonV2
              btnStyle={`w-fit px-3.5 py-2 ${
                scrolled ||
                pathname === "/privacy-policy" ||
                pathname === "/search"
                  ? "border-globalGreen"
                  : "border-borderColor"
              } rounded-3xl border flex items-center justify-center`}
              textStyle={`${
                scrolled ||
                pathname === "/privacy-policy" ||
                pathname === "/search"
                  ? "text-globalGreen"
                  : "text-white"
              } font-medium text-sm`}
              title="Login"
              handleClick={() => navigate("/login", { replace: true })}
            />

            <ButtonV2
              btnStyle="w-fit px-5 py-2 rounded-3xl gap-2 bg-globalGreen flex items-center justify-center"
              textStyle="text-white font-medium text-sm"
              title="Sign up"
              image={CHEVRON_RIGHT_WHITE}
              handleClick={() => {}}
            />
          </div>
        </div>
      )}
    </>
  );
}

export const Footer = () => {
  return (
    <footer
      id="footer"
      className="w-full h-full lg:py-20 py-10 flex items-center flex-col bg-globalGreen gap-y-10 lg:gap-y-20 justify-center"
    >
      <div className="w-full px-4 sm:px-8 lg:px-20 flex lg:flex-row flex-col gap-y-8 lg:gap-y-10 items-start justify-between">
        <div className="flex flex-col gap-y-4 items-start lg:w-[370px] w-full lg:-mt-14 -mt-0">
          <img
            src={CUSTOMARY_LOGO}
            className="w-[45px] h-[45px]"
            alt="Customary Court Logo"
          />
          <span className="text-white text-sm font-normal text-justify leading-relaxed">
            The Enugu State Customary Court of Appeal is dedicated to hearing
            and determining appeals on matters of customary law from lower
            Customary Courts across the state. We ensure justice is delivered in
            accordance with the customs, traditions, and laws of the people,
            while upholding the Constitution of the Federal Republic of Nigeria.
          </span>
          <div className="flex items-center gap-x-3">
            <img
              src={TWITTER}
              className="w-[24.01px] h-[24.01px] cursor-pointer hover:opacity-80 transition-opacity"
              alt="Twitter"
            />
            <img
              src={INSTAGRAM}
              className="w-[24.01px] h-[24.01px] cursor-pointer hover:opacity-80 transition-opacity"
              alt="Instagram"
            />
            <img
              src={LINKEDIN}
              className="w-[24.01px] h-[24.01px] cursor-pointer hover:opacity-80 transition-opacity"
              alt="LinkedIn"
            />
            <img
              src={FACEBOOK}
              className="w-[24.01px] h-[24.01px] cursor-pointer hover:opacity-80 transition-opacity"
              alt="Facebook"
            />
          </div>
        </div>

        {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 w-full lg:w-auto"> */}
        <FooterLinks title={footer[0].title} links={footer[0].links} />
        <FooterLinks title={footer[1].title} links={footer[1].links} />
        <FooterLinks title={footer[2].title} links={footer[2].links} />
        {/* </div> */}
      </div>

      <div className="w-full px-4 sm:px-8 lg:px-20 border-t-[0.5px] flex items-center justify-center pt-5 text-white font-medium text-xs sm:text-sm border-[#FFFFFF] text-center">
        <span>
          Copyright © 2025 Enugu State Customary Court of Appeal — Powered by
          LexTech
        </span>
      </div>
    </footer>
  );
};
